import { RefObject, useCallback, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { TimelineScale } from './types';
import { moveCalendarAnchor } from './calendarViewUtils';

type CalendarWindow = { start: number; end: number };

export const useCalendarInfiniteScroll = (
    scrollRef: RefObject<HTMLDivElement>, anchorDate: Date, scale: TimelineScale
) => {
    const [windowRange, setWindowRange] = useState<CalendarWindow>({ start: -2, end: 2 });
    const [activeDate, setActiveDate] = useState<Date | null>(null);
    const [activeLabel, setActiveLabel] = useState('');
    const [revision, setRevision] = useState(0);
    const centerRef = useRef(true);
    const anchorRef = useRef<{ date: string; top: number } | null>(null);
    const lockRef = useRef(false);
    const periods = useMemo(() => Array.from(
        { length: windowRange.end - windowRange.start + 1 },
        (_, index) => {
            const offset = windowRange.start + index;
            return { date: moveCalendarAnchor(anchorDate, scale, offset), offset };
        }
    ), [anchorDate, scale, windowRange]);
    const updateActiveLabel = useCallback(() => {
        const container = scrollRef.current;
        if (!container) return;
        const toolbar = container.querySelector('.timeline-zoom-controls') as HTMLElement | null;
        const line = container.getBoundingClientRect().top + (toolbar?.offsetHeight || 0) + 16;
        const entries = Array.from(container.querySelectorAll('.calendar-infinite-period')) as HTMLElement[];
        let label = entries[0]?.dataset.calendarLabel || '';
        let date = entries[0]?.dataset.calendarDate || '';
        entries.forEach((entry) => {
            if (entry.getBoundingClientRect().top <= line) {
                label = entry.dataset.calendarLabel || label;
                date = entry.dataset.calendarDate || date;
            }
        });
        setActiveLabel((current) => current === label ? current : label);
        setActiveDate((current) => current?.toISOString() === date ? current : (date ? new Date(date) : null));
    }, [scrollRef]);
    const reset = useCallback(() => {
        centerRef.current = true;
        anchorRef.current = null;
        lockRef.current = false;
        setWindowRange({ start: -2, end: 2 });
        setRevision((current) => current + 1);
    }, []);
    useLayoutEffect(() => {
        const container = scrollRef.current;
        if (!container) return;
        if (anchorRef.current) {
            const anchor = anchorRef.current;
            const entry = (Array.from(container.querySelectorAll('.calendar-infinite-period')) as HTMLElement[])
                .find((period) => period.dataset.calendarDate === anchor.date);
            if (entry) container.scrollTop += entry.getBoundingClientRect().top
                - container.getBoundingClientRect().top - anchor.top;
            anchorRef.current = null;
            window.setTimeout(() => { lockRef.current = false; }, 80);
        } else if (centerRef.current) {
            const current = container.querySelector('[data-calendar-current="true"]') as HTMLElement | null;
            const toolbar = container.querySelector('.timeline-zoom-controls') as HTMLElement | null;
            if (current) {
                const delta = current.getBoundingClientRect().top - container.getBoundingClientRect().top
                    - (toolbar?.offsetHeight || 0) - 8;
                container.scrollTop = Math.max(0, container.scrollTop + delta);
            }
            centerRef.current = false;
        }
        window.setTimeout(updateActiveLabel, 0);
    }, [periods, revision, scrollRef, updateActiveLabel]);
    useLayoutEffect(() => {
        const container = scrollRef.current;
        if (!container) return undefined;
        const onScroll = () => {
            updateActiveLabel();
            if (lockRef.current) return;
            const remaining = container.scrollHeight - container.scrollTop - container.clientHeight;
            const top = container.scrollTop < 360;
            const bottom = remaining < 360;
            if (top || bottom) {
                lockRef.current = true;
                const rect = container.getBoundingClientRect();
                const visible = (Array.from(container.querySelectorAll('.calendar-infinite-period')) as HTMLElement[])
                    .filter((period) => period.getBoundingClientRect().bottom > rect.top);
                // When moving down the first visible month is removed. Anchor to the
                // following one so the scroll correction always has a retained node.
                const entry = bottom ? visible[1] || visible[0] : visible[0];
                if (entry?.dataset.calendarDate) anchorRef.current = {
                    date: entry.dataset.calendarDate, top: entry.getBoundingClientRect().top - rect.top,
                };
                setWindowRange((range) => top
                    ? { start: range.start - 1, end: range.end - 1 }
                    : { start: range.start + 1, end: range.end + 1 });
                window.setTimeout(() => {
                    if (anchorRef.current) {
                        anchorRef.current = null;
                        lockRef.current = false;
                    }
                }, 500);
            }
        };
        container.addEventListener('scroll', onScroll);
        return () => container.removeEventListener('scroll', onScroll);
    }, [scrollRef, updateActiveLabel]);
    return { activeDate, activeLabel, periods, reset };
};
